import React from 'react';
import { Link } from 'react-router-dom';
import { LegalLinks } from '@/pages/legal/LegalLayout';
import { useTheme } from '@/contexts/ThemeContext';
import { Dumbbell, TrendingUp, Target, Users, Zap, Award, CheckCircle, BarChart2, Bell, Shield, Sun, Moon } from 'lucide-react';

const features = [
  { icon: TrendingUp, title: 'Track Progress', description: 'Log every workout and visualize your fitness journey with detailed charts, personal records, and weekly summaries.' },
  { icon: Target, title: 'Set Goals', description: 'Define weight, strength, or cardio goals. Get smart reminders and milestone alerts to keep you on track.' },
  { icon: Users, title: 'Community', description: 'Follow friends, share achievements, comment on workouts, and push each other to new heights.' },
  { icon: Zap, title: 'AI Coach', description: 'Receive personalized workout plans and recovery tips generated from your actual performance data.' },
  { icon: Award, title: 'FitCoins', description: 'Earn coins for consistency, streaks, and hitting milestones. Redeem them for rewards and badges.' },
  { icon: Dumbbell, title: 'Exercise Library', description: 'Browse 1,600+ exercises with photo demos, muscle and equipment filters, and step-by-step form guides.' },
  { icon: BarChart2, title: 'Analytics Dashboard', description: 'Deep-dive into volume, frequency, and intensity trends across any time range with interactive graphs.' },
  { icon: Bell, title: 'Smart Reminders', description: 'Never miss a session. Get push notifications for scheduled workouts, rest days, and goal deadlines.' },
  { icon: Shield, title: 'Private & Secure', description: 'Your data stays yours. Full control over what you share publicly, with friends, or keep private.' },
];

// Product facts only — no user counts or ratings we can't back up.
const stats = [
  { value: '1,600+', label: 'Exercises with guides' },
  { value: '24/7', label: 'AI coach' },
  { value: '$0', label: 'To get started' },
  { value: 'Any', label: 'Device, any browser' },
];

const howItWorks = [
  { step: '01', title: 'Create your profile', description: 'Sign up in seconds and set your fitness level, goals, and preferred workout types.' },
  { step: '02', title: 'Log your workouts', description: 'Add exercises, sets, reps, and weights. FitTrack saves your history automatically.' },
  { step: '03', title: 'Track & improve', description: 'Review your analytics, hit milestones, and let the AI coach adapt your plan over time.' },
];

// Capabilities we can demonstrate on request — not customer quotes. The three
// invented testimonials that used to sit here made a claim we cannot support.
const proofPoints = [
  {
    icon: BarChart2,
    title: 'Every session, kept',
    body: 'Sets, reps, weight and duration are stored per workout, so personal records and volume trends come from your own history rather than an estimate.',
  },
  {
    icon: Zap,
    title: 'A coach that reads your data',
    body: 'Ask how many steps you walked today or what to train next and the answer is drawn from your logged workouts, goals and connected tracker.',
  },
  {
    icon: Shield,
    title: 'Your data stays yours',
    body: 'Connect Google Health or nothing at all. Workouts are private by default, and you choose what gets shared to your profile or the community.',
  },
];

const LandingPage: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const dark = theme === 'dark';

  return (
    <div className="min-h-screen bg-app text-ink">
      {/* Navbar */}
      <nav className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
        <div className="flex items-center gap-2">
          <Dumbbell className="h-7 w-7 text-accent" />
          <span className="text-xl font-bold text-ink">FitTrack</span>
        </div>
        <div className="hidden sm:flex items-center gap-6 text-sm text-ink-dim">
          <a href="#features" className="hover:text-ink transition-colors">Features</a>
          <a href="#how-it-works" className="hover:text-ink transition-colors">How it works</a>
          <a href="#testimonials" className="hover:text-ink transition-colors">Why FitTrack</a>
        </div>
        <div className="flex items-center gap-2">
          {/* Theme toggle */}
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg transition-colors text-ink-dim hover:text-ink hover:bg-elevated"
            title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {dark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <Link to="/login" className="text-sm px-4 py-2 transition-colors text-ink-muted hover:text-ink">
            Sign In
          </Link>
          <Link to="/register" className="text-sm bg-primary-600 hover:bg-primary-500 transition-colors text-white px-4 py-2 rounded-lg font-medium">
            Get Started
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative px-6 pt-20 pb-28 text-center max-w-4xl mx-auto">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary-600/20 rounded-full blur-3xl" />
        </div>
        <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent mb-4">Your fitness, your way</span>
        <h1 className="text-5xl sm:text-6xl font-extrabold leading-tight mb-6">
          Train smarter.<br />
          <span className="text-accent">Track everything.</span>
        </h1>
        <p className="text-lg max-w-xl mx-auto mb-6 text-ink-dim">
          FitTrack brings together workout logging, goal setting, an AI coach, and a community — all in one place. Free forever for the basics.
        </p>
        <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm mb-10 text-ink-dim">
          {['No credit card required', 'Works on any device', 'Cancel anytime'].map(item => (
            <li key={item} className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4 text-accent" />
              {item}
            </li>
          ))}
        </ul>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/register" className="bg-primary-600 hover:bg-primary-500 transition-colors text-white px-8 py-3 rounded-xl font-semibold text-base">
            Start for free
          </Link>
          <Link to="/login" className="border border-line hover:border-ink-faint transition-colors px-8 py-3 rounded-xl font-semibold text-base text-ink-muted hover:text-ink">
            Sign in
          </Link>
        </div>
      </section>

      {/* Stats */}
      <section className="px-6 pb-20 max-w-4xl mx-auto">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {stats.map(({ value, label }) => (
            <div key={label} className="border border-line bg-surface rounded-2xl p-6">
              <div className="text-3xl font-extrabold text-accent mb-1">{value}</div>
              <div className="text-sm text-ink-dim">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="px-6 pb-24 max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-3">Everything you need to reach your goals</h2>
          <p className="max-w-xl mx-auto text-ink-dim">From beginner to elite athlete, FitTrack has the tools to support every stage of your journey.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map(({ icon: Icon, title, description }) => (
            <div key={title} className="border border-line bg-surface rounded-2xl p-6 hover:border-accent/40 transition-colors">
              <div className="w-10 h-10 bg-primary-600/20 rounded-lg flex items-center justify-center mb-4">
                <Icon className="h-5 w-5 text-accent" />
              </div>
              <h3 className="font-semibold mb-2">{title}</h3>
              <p className="text-sm leading-relaxed text-ink-dim">{description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="px-6 pb-24 max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-3">How it works</h2>
          <p className="text-ink-dim">Up and running in under 2 minutes.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
          {howItWorks.map(({ step, title, description }) => (
            <div key={step} className="text-center">
              <div className="text-5xl font-extrabold text-primary-600/40 mb-3">{step}</div>
              <h3 className="font-semibold mb-2">{title}</h3>
              <p className="text-sm leading-relaxed text-ink-dim">{description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What you get */}
      <section id="testimonials" className="px-6 pb-24 max-w-5xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-3">Built around your own numbers</h2>
          <p className="text-ink-dim">No guesswork, no generic plans.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {proofPoints.map(({ icon: Icon, title, body }) => (
            <div key={title} className="border border-line bg-surface rounded-2xl p-6">
              <div className="w-10 h-10 bg-primary-600/15 rounded-lg flex items-center justify-center mb-4">
                <Icon className="h-5 w-5 text-accent" />
              </div>
              <h3 className="font-semibold mb-2">{title}</h3>
              <p className="text-sm leading-relaxed text-ink-dim">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24 text-center">
        <div className="max-w-xl mx-auto border border-accent/25 rounded-3xl p-12 bg-gradient-to-br from-primary-500/15 to-primary-700/10">
          <h2 className="text-3xl font-bold mb-4">Ready to level up?</h2>
          <p className="mb-8 text-ink-dim">Start tracking your workouts, goals and progress today. Free to start, no strings attached.</p>
          <Link to="/register" className="inline-block bg-primary-600 hover:bg-primary-500 transition-colors text-white px-8 py-3 rounded-xl font-semibold">
            Create your free account
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-line px-6 py-8 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-4 text-sm text-ink-dim">
          <div className="flex items-center gap-2">
            <Dumbbell className="h-4 w-4" />
            <span>FitTrack</span>
          </div>
          <div className="flex gap-6">
            <a href="#features" className="hover:text-ink-dim transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-ink-dim transition-colors">How it works</a>
            <a href="#testimonials" className="hover:text-ink transition-colors">Why FitTrack</a>
          </div>
          <LegalLinks />
          <span>© {new Date().getFullYear()} FitTrack. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
